import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Box,
    IconButton,
    List,
    ListItem,
    ListItemText
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

const ProductDetailsModal = ({
    open,
    onClose,
    product
}) => {
    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    bgcolor: "#3e63ec",
                    color: "white",
                    px: 3,
                    py: 2,
                    borderTopLeftRadius: 8,
                    borderTopRightRadius: 8,
                }}
            >
                <MDTypography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ letterSpacing: 0.5, color: "#ffffff" }}
                >
                    {product?.product_name || "Product Details"}
                </MDTypography>
                <IconButton onClick={onClose} sx={{ color: "#fff" }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent
                dividers
                sx={{ backgroundColor: "#f4f6f8", px: 4, py: 3 }}
            >
                <Grid container spacing={2}>
                    {[
                        { label: "📂 Category", value: product?.category_name },
                        { label: "🆔 SKU", value: product?.sku },
                        { label: "💰 Price", value: `₹${product?.price}`, color: "green" },
                        {
                            label: "🏷 Discounted Price",
                            value: product?.discount_price ? `₹${product.discount_price}` : "N/A",
                            color: "primary",
                        },
                        {
                            label: "📦 Stock Quantity",
                            value: product?.stock_quantity ?? "N/A",
                            color: product?.stock_quantity > 0 ? "green" : "error",
                        },
                        {
                            label: "🎯 Discount",
                            value: `${product?.discount ?? 0}%`,
                            color: "secondary",
                        },
                        { label: "📑 GST Type", value: product?.gst_type },
                        product?.gst_type === "Include GST" && {
                            label: "📊 GST %",
                            value: `${product?.gst_percentage ?? 0}%`
                        },
                        product?.gst_type === "Exclude GST" && {
                            label: "📊 GST %",
                            value: `${product?.gst_percentage ?? 0}%`
                        },
                    ].filter(Boolean).map((item, i) => (
                        <Grid item xs={6} key={i}>
                            <Box
                                sx={{
                                    bgcolor: "#fff",
                                    p: 2,
                                    borderRadius: 2,
                                    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                                    height: "100%",
                                }}
                            >
                                <MDTypography variant="caption" color="text.secondary">
                                    {item.label}
                                </MDTypography>
                                <MDTypography
                                    variant="body2"
                                    fontWeight={600}
                                    color={item.color ? item.color : "text.primary"}
                                >
                                    {item.value || "N/A"}
                                </MDTypography>
                            </Box>
                        </Grid>
                    ))}

                    {/* Keywords */}
                    <Grid item xs={12}>
                        <Box
                            sx={{
                                bgcolor: "#fff",
                                p: 2,
                                borderRadius: 2,
                                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                            }}
                        >
                            <MDTypography variant="caption" color="text.secondary">
                                🔑 Keywords
                            </MDTypography>
                            <MDTypography variant="body2" fontWeight={500}>
                                {product?.keywords || "N/A"}
                            </MDTypography>
                        </Box>
                    </Grid>

                    {/* Description */}
                    <Grid item xs={12}>
                        <Box
                            sx={{
                                bgcolor: "#fff",
                                p: 2,
                                borderRadius: 2,
                                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                            }}
                        >
                            <MDTypography variant="caption" color="text.secondary">
                                📝 Description
                            </MDTypography>
                            <MDTypography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                                {product?.description || "No description provided."}
                            </MDTypography>
                        </Box>
                    </Grid>

                    {/* Specifications */}
                    {Array.isArray(product?.specification) && product.specification.length > 0 && (
                        <Grid item xs={12}>
                            <Box
                                sx={{
                                    bgcolor: "#fff",
                                    p: 2,
                                    borderRadius: 2,
                                    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                                }}
                            >
                                <MDTypography variant="caption" color="text.secondary" gutterBottom>
                                    ⚙️ Specifications
                                </MDTypography>
                                <List dense disablePadding>
                                    {product.specification.map((spec, index) => (
                                        <ListItem
                                            key={index}
                                            sx={{
                                                py: 1,
                                                borderBottom: index < product.specification.length - 1 ? "1px dashed #eee" : "none",
                                            }}
                                        >
                                            <ListItemText
                                                primary={<MDTypography fontWeight={600} variant="body2">{spec.title}</MDTypography>}
                                                secondary={<MDTypography variant="caption">{spec.desc}</MDTypography>}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>

            <DialogActions
                sx={{
                    backgroundColor: "#fafafa",
                    px: 3,
                    py: 2,
                    borderBottomLeftRadius: 8,
                    borderBottomRightRadius: 8,
                }}
            >
                <MDButton onClick={onClose} variant="contained" color="secondary">
                    Close
                </MDButton>
            </DialogActions>
        </Dialog>
    );
};

export default ProductDetailsModal;